// src/pages/borrower/ForgotPassword.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/ui/Card.jsx";
import { Mail, Send, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { resetPassword } from "../../api/borrowerPortal/resetPassword.js";

export default function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      return setError("Please enter your email.");
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailTrimmed)) {
      return setError("Please enter a valid email address.");
    }

    setLoading(true);

    try {
      await resetPassword(emailTrimmed);
      // Success - show success message
      setSent(true);
    } catch (err) {
      console.error("Reset password error:", err);
      
      // Extract error message from various possible formats
      let errorMessage = "Failed to send reset email. Please try again later.";
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.exc || errorData?.exception) {
          errorMessage = errorData.exc || errorData.exception;
        } else if (errorData?._server_messages) {
          try {
            const serverMsgs = typeof errorData._server_messages === "string"
              ? JSON.parse(errorData._server_messages)
              : errorData._server_messages;
            if (Array.isArray(serverMsgs) && serverMsgs.length > 0) {
              const firstMsg = typeof serverMsgs[0] === "string" ? JSON.parse(serverMsgs[0]) : serverMsgs[0];
              if (firstMsg?.message) {
                errorMessage = firstMsg.message;
              }
            }
          } catch (parseErr) {
            // If parsing fails, use default message
          }
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes slowFloat { 0% { transform: translateY(0) } 50% { transform: translateY(-6px) } 100% { transform: translateY(0) } }
        .logo-float { animation: slowFloat 6s ease-in-out infinite; }
        @keyframes glow { 0% { box-shadow: 0 0 0 rgba(56,189,248,0.0) } 50% { box-shadow: 0 8px 24px rgba(56,189,248,0.25) } 100% { box-shadow: 0 0 0 rgba(56,189,248,0.0) } }
        .card-glow { animation: glow 6s ease-in-out infinite; }
      `}</style>

      <div className="min-h-screen grid md:grid-cols-2 bg-slate-50">
        {/* Brand / Left panel */}
        <div className="hidden md:flex relative items-center justify-start overflow-hidden pl-10">
          <div className="absolute inset-0 bg-gradient-to-b from-sky-500 to-sky-600" />
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 -right-10 h-72 w-72 rounded-full bg-white/10 blur-2xl" />

          <div className="relative z-10 w-full max-w-4xl pr-6 text-white">
            <div className="flex items-center gap-0">
              <a href="/" className="shrink-0 -mr-3 md:-mr-5">
                <img
                  src="/dynesourcer-logo.svg"
                  alt="Dynesourcer polar bear"
                  className="h-36 md:h-48 w-auto logo-float select-none"
                  draggable="false"
                  loading="eager"
                  width="192"
                  height="192"
                />
              </a>
              <div className="min-w-0">
                <h1 className="md:text-5xl text-3xl font-extrabold tracking-tight">
                  Dynesourcer
                </h1>
                <p className="text-white/85 md:text-base text-sm mt-2">
                  Borrower Application Portal
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <h2 className="text-3xl font-bold leading-tight">Reset your password</h2>
              <p className="text-white/85">
                Enter the email associated with your account. If it exists, we'll send password reset
                instructions.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
                <p className="font-semibold">Secure</p>
                <p className="text-white/80">Your data, locked tight.</p>
              </div>
              <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
                <p className="font-semibold">Simple</p>
                <p className="text-white/80">Reset with confidence.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Reset Card / Right panel */}
        <div className="flex items-center justify-center p-6">
          <Card className="w-full max-w-md rounded-2xl border-slate-200 bg-white p-6 shadow-sm card-glow">
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-sky-500" />
                <h2 className="text-xl font-semibold text-slate-900">Forgot password</h2>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                We’ll email you a link to create a new password.
              </p>
            </div>

            {sent ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                  If an account exists for <span className="font-semibold">{email}</span>, a reset link has been sent.
                  Please check your inbox (and spam).
                </div>
                <button
                  onClick={() => navigate("/borrower/signin")}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-slate-700">Email</span>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 pr-10 outline-none focus:ring-2 focus:ring-sky-200"
                      placeholder="you@dynesourcer.com"
                      autoComplete="email"
                      disabled={loading}
                      aria-invalid={!!error && !email.trim()}
                    />
                    <Mail className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </label>

                {error && <p className="text-sm text-rose-600">{error}</p>}

                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 font-semibold text-white shadow hover:bg-sky-600 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send reset link
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/borrower/signin")}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
